import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { getSubjectPillClass, resolveSubjectName } from '@/lib/subjectVisuals';
import {
  ArrowLeft, MessageCircle, Mic, Camera, Clock, ChevronRight,
  Sparkles, BookOpen, Volume2, Search, Filter
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';
import useHomeRoute from '@/hooks/useHomeRoute';

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

const MODE_ICONS = { text: MessageCircle, voice: Mic, image: Camera };

export default function ParentChatView() {
  const { childId, conversationId } = useParams();
  const navigate = useNavigate();
  const homeRoute = useHomeRoute();
  const [child, setChild] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [selectedConv, setSelectedConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState(conversationId ? 'messages' : 'list');

  useEffect(() => {
    fetchConversations();
  }, [childId]);

  useEffect(() => {
    if (conversationId) {
      loadConversation(conversationId);
      setViewMode('messages');
    }
  }, [conversationId]);

  const fetchConversations = async () => {
    try {
      const res = await api.get(`/chat/parent/${childId}/conversations`);
      setChild(res.data.child);
      setConversations(res.data.conversations);
    } catch (err) {
      console.error('Failed to fetch conversations:', err);
      toast.error('Failed to load conversations');
    } finally {
      setLoading(false);
    }
  };

  const loadConversation = async (convId) => {
    try {
      const res = await api.get(`/chat/parent/conversation/${convId}`);
      setSelectedConv(res.data.conversation);
      setMessages(res.data.messages);
      setViewMode('messages');
    } catch (err) {
      console.error('Failed to load conversation:', err);
      toast.error('Failed to load conversation');
    }
  };

  const goBack = () => {
    if (viewMode === 'messages') {
      setViewMode('list');
      setSelectedConv(null);
      setMessages([]);
      navigate(`/parent/child/${childId}/chats`);
    } else {
      navigate(homeRoute);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-3 border-orange-300 border-t-orange-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 max-w-2xl mx-auto" data-testid="parent-chat-view">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 p-4 sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <button data-testid="parent-chat-back" onClick={goBack} className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors">
            <ArrowLeft className="w-4 h-4 text-gray-600" />
          </button>
          <div>
            <h2 className="font-semibold text-gray-800">
              {viewMode === 'messages' && selectedConv
                ? selectedConv.title || 'Conversation'
                : `${child?.name || 'Child'}'s Conversations`}
            </h2>
            <p className="text-xs text-gray-500">
              {viewMode === 'messages'
                ? `${messages.length} messages`
                : `${conversations.length} conversations`}
            </p>
          </div>
        </div>
      </div>

      {/* Conversations List */}
      {viewMode === 'list' && (
        <div className="p-4 space-y-3">
          {conversations.length === 0 ? (
            <div className="text-center py-16">
              <MessageCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No conversations yet</p>
              <p className="text-gray-400 text-sm">Your child hasn't chatted with Yukti yet.</p>
            </div>
          ) : (
            conversations.map((conv, i) => (
              <motion.div
                key={conv.conversation_id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.04 }}
                onClick={() => loadConversation(conv.conversation_id)}
                data-testid={`parent-conv-${conv.conversation_id}`}
                className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 cursor-pointer hover:shadow-md hover:border-orange-200 transition-all"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-gray-800 text-sm truncate">{conv.title || 'New Conversation'}</h3>
                    <div className="flex items-center gap-3 mt-1.5">
                      {conv.subject && (
                        <span className={`text-xs px-2 py-0.5 rounded-full ${getSubjectPillClass(conv.subject)}`}>{resolveSubjectName(conv.subject) || conv.subject}</span>
                      )}
                      <span className="text-xs text-gray-400 flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {timeAgo(conv.updated_at)}
                      </span>
                      <span className="text-xs text-gray-400">{conv.message_count || 0} msgs</span>
                    </div>
                    {conv.topics?.length > 0 && (
                      <div className="flex gap-1 mt-2 flex-wrap">
                        {conv.topics.slice(0, 4).map(t => (
                          <span key={t} className="text-[10px] bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">{t}</span>
                        ))}
                      </div>
                    )}
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-300 flex-shrink-0 mt-1" />
                </div>
              </motion.div>
            ))
          )}
        </div>
      )}

      {/* Messages View (Read-only) */}
      {viewMode === 'messages' && (
        <div className="p-4 space-y-4">
          {selectedConv?.subject && (
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs bg-orange-100 text-orange-600 px-3 py-1 rounded-full font-medium">
                {selectedConv.subject}
              </span>
              {selectedConv.topics?.map(t => (
                <span key={t} className="text-xs bg-gray-100 text-gray-500 px-2 py-1 rounded-full">{t}</span>
              ))}
            </div>
          )}

          {messages.map((msg, i) => {
            const ModeIcon = MODE_ICONS[msg.content_type] || MessageCircle;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.03 }}
                className={`flex ${msg.role === 'child' ? 'justify-end' : 'justify-start'}`}
              >
                {msg.role === 'yukti' ? (
                  <div className="max-w-[85%]">
                    <div className="bg-white rounded-xl p-3 shadow-sm border border-gray-100">
                      <div className="flex items-center gap-1.5 mb-1.5">
                        <Sparkles className="w-3.5 h-3.5 text-orange-400" />
                        <span className="text-[11px] font-semibold text-orange-500">Yukti</span>
                      </div>
                      <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line">{msg.text}</p>
                    </div>
                  </div>
                ) : (
                  <div className="max-w-[80%]">
                    <div className="bg-orange-500 text-white rounded-xl px-3 py-2.5 shadow-sm">
                      <div className="flex items-center gap-1.5">
                        <ModeIcon className="w-3 h-3 opacity-70" />
                        <p className="text-sm">{msg.text}</p>
                      </div>
                    </div>
                    <p className="text-[10px] text-gray-400 mt-0.5 text-right">
                      {new Date(msg.timestamp).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
}
